import React from "react";
import { motion } from "framer-motion";

// ====== Existing Result (Board of Directors 19M) ======
import resultImage from "../assets/election-result.png";
import resultPDF from "../assets/election-result.pdf";

// ====== NEW: Office Bearers Approval (Chairman & Vice-Chairman - 19N) ======
import officeBearersImage from "../assets/office-bearers-result.png";   // <-- add this PNG/JPG
import officeBearersPDF from "../assets/office-bearers-result.pdf";     // <-- add this PDF

function ElectionResults() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 py-10 px-4 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto space-y-10 lg:space-y-12">
        {/* Page Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-amber-700"
        >
          🏆 Election Results
        </motion.h1>

        {/* ====== SECTION 1: Board of Directors Result (19M) ====== */}
        <motion.section
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/90 backdrop-blur-lg border-l-[10px] border-yellow-600 shadow-2xl rounded-2xl px-6 py-8 lg:px-10 lg:py-10"
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left: Image Preview */}
            <div className="flex flex-col items-center">
              <div className="rounded-xl border shadow-xl w-full overflow-hidden">
                <div className="w-full h-[55vh] lg:h-[60vh] max-h-[70vh] overflow-hidden bg-white">
                  <img
                    src={resultImage}
                    alt="Board of Directors Election Result (19M)"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <a
                href={resultImage}
                download="BoardOfDirectorsResult.png"
                className="mt-4 bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-full font-semibold shadow-md transition"
              >
                📥 Download Result Image
              </a>
            </div>

            {/* Right: Details + PDF */}
            <div className="space-y-5 text-[16px] text-gray-800 leading-relaxed">
              <h2 className="text-2xl font-bold text-yellow-800">
                Board of Directors – Final Result (Format 19M)
              </h2>
              <p>
                Shine Agri Allied & Marketing Multistate Cooperative Society Ltd
                के Board of Directors का आधिकारिक परिणाम जारी हो चुका है। Returning
                Officer ने <strong>04 September 2025</strong> को घोषणा की।
              </p>
              <p>
                सदस्य MSCS गाइडलाइन्स के अनुसार निर्वाचित/निर्विरोध चुने गये हैं।
              </p>
              <div className="bg-yellow-50 border border-yellow-200 p-5 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-yellow-800 mb-3">
                  📄 Download Final Result (PDF)
                </h3>
                <a
                  href={resultPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-yellow-700 hover:bg-yellow-800 text-white px-6 py-2 rounded-full font-semibold shadow-md transition"
                >
                  View / Download PDF
                </a>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ====== SECTION 2: Office Bearers (Chairman & Vice-Chairman) 19N ====== */}
        <motion.section
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="bg-white/90 backdrop-blur-lg border-l-[10px] border-amber-700 shadow-2xl rounded-2xl px-6 py-8 lg:px-10 lg:py-10"
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left: Description + PDF */}
            <div className="order-2 lg:order-1 space-y-5 text-[16px] text-gray-800 leading-relaxed">
              <h2 className="text-2xl font-extrabold text-amber-800">
                Office Bearers – Chairman & Vice-Chairman (Format 19N)
              </h2>
              <p>
                Cooperative Election Authority, New Delhi द्वारा Rule 19N के
                अंतर्गत <strong>Chairman</strong> और <strong>Vice-Chairman</strong> की
                नियुक्ति/अनुमोदन जारी किया गया है। यह आदेश Returning Officer की
                सिफारिशों पर आधारित है।
              </p>
              <ul className="list-disc ml-6 text-gray-700">
                <li>Chairman: SAAM-2025-0091 (Unopposed)</li>
                <li>Vice-Chairman: SAAM-2025-0053 (Unopposed)</li>
              </ul>

              <div className="bg-amber-50 border border-amber-200 p-5 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-amber-900 mb-3">
                  📄 Download Approval (PDF)
                </h3>
                <a
                  href={officeBearersPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full font-semibold shadow-md transition"
                >
                  View / Download PDF
                </a>
              </div>
            </div>

            {/* Right: Image Preview */}
            <div className="order-1 lg:order-2 flex flex-col items-center">
              <div className="rounded-xl border shadow-xl w-full overflow-hidden">
                <div className="w-full h-[55vh] lg:h-[60vh] max-h-[70vh] overflow-hidden bg-white">
                  <img
                    src={officeBearersImage}
                    alt="Office Bearers Approval (Chairman & Vice-Chairman - 19N)"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <a
                href={officeBearersImage}
                download="OfficeBearersApproval.png"
                className="mt-4 bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full font-semibold shadow-md transition"
              >
                📥 Download Approval Image
              </a>
            </div>
          </div>
        </motion.section>

        {/* Small footer note (optional) */}
        <div className="text-center text-sm text-gray-500">
          * Desktop पर दोनों सेक्शन एक ही स्क्रीन में फिट होने के लिए heights सीमित रखी गयी हैं।
        </div>
      </div>
    </div>
  );
}

export default ElectionResults;
